
import { useState, useEffect } from 'react';
import Button from '../base/Button';

export default function Navigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Mobil menü açıkken body scroll'unu engelle
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup function
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  const navItems = [
    { id: 'ana-sayfa', label: 'Ana Sayfa' },
    { id: 'ozellikler', label: 'Özellikler' },
    { id: 'hizmetler', label: 'Hizmetler' },
    { id: 'süreç', label: 'Süreç' },
    { id: 'hakkimizda', label: 'Hakkımızda' },
    { id: 'referanslar', label: 'Referanslar' },
    { id: 'fiyatlar', label: 'Fiyatlar' },
    { id: 'iletisim', label: 'İletişim' }
  ];

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-gray-900/95 backdrop-blur-md shadow-2xl' : 'bg-transparent'
    }`}>
      <div className="px-4 sm:px-6 mx-auto max-w-7xl">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo */}
          <div 
            className="flex items-center cursor-pointer group"
            onClick={() => scrollToSection('ana-sayfa')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                scrollToSection('ana-sayfa');
              }
            }}
            aria-label="Ana sayfaya git"
          >
            <div className="flex items-center space-x-2">
              <img 
                src="https://static.readdy.ai/image/affc4fc5264ff833cc1aa53e66659722/b87eb8ba8acad28110956199bb423d54.png" 
                alt="AYYILDIZ DİGİTAL Logo" 
                className="w-8 h-8 sm:w-10 sm:h-10 object-contain transition-all duration-300 group-hover:scale-110 mix-blend-normal"
                style={{ backgroundColor: 'transparent' }}
              />
              <div className="text-sm sm:text-base font-bold bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-500 bg-clip-text text-transparent transition-all duration-300 group-hover:scale-105 whitespace-nowrap">
                AYYILDIZ DİGİTAL
              </div>
            </div>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-6 xl:space-x-8">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className="text-gray-300 hover:text-blue-400 transition-all duration-300 font-medium relative group whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-blue-400 rounded px-2 py-1"
                aria-label={`${item.label} bölümüne git`}
              >
                {item.label}
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-blue-400 to-blue-600 transition-all duration-300 group-hover:w-full"></span>
              </button>
            ))}
            <Button 
              onClick={() => scrollToSection('iletisim')}
              className="ml-4 whitespace-nowrap"
              aria-label="Teklif almak için iletişim bölümüne git"
            >
              Teklif Al
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden text-white focus:outline-none focus:ring-2 focus:ring-blue-400 rounded p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? 'Menüyü kapat' : 'Menüyü aç'}
            aria-expanded={isMobileMenuOpen}
          >
            <i className={`ri-${isMobileMenuOpen ? 'close' : 'menu'}-line text-xl sm:text-2xl`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <>
            {/* Backdrop */}
            <div 
              className="fixed inset-0 bg-black/50 z-40 lg:hidden"
              onClick={() => setIsMobileMenuOpen(false)}
              aria-hidden="true"
            />
            
            {/* Menu Panel */}
            <div className="fixed inset-x-0 top-0 z-50 lg:hidden">
              <div className="bg-gray-900 shadow-2xl border-b border-gray-700">
                <div className="px-4 py-6">
                  {/* Mobile Menu Header */}
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-3">
                      <img 
                        src="https://static.readdy.ai/image/affc4fc5264ff833cc1aa53e66659722/b87eb8ba8acad28110956199bb423d54.png" 
                        alt="AYYILDIZ DİGİTAL Logo" 
                        className="w-10 h-10 object-contain mix-blend-normal"
                        style={{ backgroundColor: 'transparent' }}
                      />
                      <span className="text-base font-bold bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-500 bg-clip-text text-transparent">
                        AYYILDIZ DİGİTAL
                      </span>
                    </div>
                    <button
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="text-gray-300 hover:text-white focus:outline-none focus:ring-2 focus:ring-blue-400 rounded p-2"
                      aria-label="Menüyü kapat"
                    >
                      <i className="ri-close-line text-xl"></i>
                    </button>
                  </div>

                  {/* Mobile Menu Items */}
                  <nav className="space-y-2" role="navigation" aria-label="Mobil navigasyon">
                    {navItems.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => scrollToSection(item.id)}
                        className="block w-full text-left text-gray-300 hover:text-blue-400 hover:bg-gray-800/50 py-3 px-4 rounded-lg transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-400"
                        aria-label={`${item.label} bölümüne git`}
                      >
                        <div className="flex items-center">
                          <i className="ri-arrow-right-s-line mr-2 transition-transform group-hover:translate-x-1"></i>
                          {item.label}
                        </div>
                      </button>
                    ))}
                    <div className="pt-4">
                      <Button 
                        onClick={() => scrollToSection('iletisim')}
                        className="w-full whitespace-nowrap"
                        aria-label="Teklif almak için iletişim bölümüne git"
                      >
                        Teklif Al
                      </Button>
                    </div>
                  </nav>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </nav>
  );
}
